import React, { useEffect, useRef, useState } from 'react';
import { Landmark, Loader2, Save } from 'lucide-react';
import type { Section, SectionBankInfo } from '@yawi/types';
import { getErrorMessage, useServices, useToast } from '@/app/providers';
import { Button } from '@/presentation/components/atoms/Button';
import { SectionBankInfoFields } from '@/presentation/components/molecules/SectionBankInfoFields';

interface SectionBankAccountCardProps {
  sectionId: string;
  /** Nombre del catálogo: dice qué se está cargando antes de tener la sección. */
  sectionName: string;
  canEdit: boolean;
}

const CARD = 'bg-white rounded-xl shadow-sm border border-slate-100 p-6';

/**
 * Cuenta bancaria propia de UNA sección. Se monta con `key={sectionId}`: cambiar de
 * sección descarta todo el estado (nunca se ve la cuenta de la anterior) y lo que
 * responda tarde una sección que ya no se muestra, carga o guardado, se ignora.
 */
export const SectionBankAccountCard = ({ sectionId, sectionName, canEdit }: SectionBankAccountCardProps) => {
  const { sectionRepo } = useServices();
  const toast = useToast();
  const [section, setSection] = useState<Section | null>(null);
  const [bankInfo, setBankInfo] = useState<SectionBankInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    sectionRepo
      .getSectionById(sectionId)
      .then((loaded) => {
        if (cancelled) return;
        if (!loaded) {
          setError(`No se pudo cargar la configuración financiera de ${sectionName}`);
          return;
        }
        setSection(loaded);
        setBankInfo(loaded.bankInfo ?? null);
      })
      .catch((e) => {
        if (!cancelled) setError(getErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [sectionRepo, sectionId, sectionName, attempt]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!section || !canEdit) return;
    setSaving(true);
    try {
      const updated = await sectionRepo.updateSectionBankInfo(section.id, bankInfo);
      toast.success(`Cuenta bancaria de ${updated.name} guardada correctamente`);
      if (!mounted.current) return; // ya se cambió de sección: no pintar esta encima
      setSection(updated);
      setBankInfo(updated.bankInfo ?? null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  if (error) {
    return (
      <div className={`${CARD} flex items-center justify-between gap-3`} role="alert">
        <p className="text-sm text-red-600">{error}</p>
        <Button size="sm" variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!section) {
    return (
      <div className={`${CARD} flex items-center gap-3 text-sm text-slate-500`} role="status" aria-live="polite">
        <Loader2 size={18} className="animate-spin text-indigo-600" />
        Cargando configuración financiera de {sectionName}...
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className={CARD}>
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-bold text-slate-800 flex items-center gap-2">
          <div className="p-1.5 bg-indigo-50 rounded text-indigo-600"><Landmark size={18} /></div>
          Cuenta bancaria · {section.name}
        </h4>
        {canEdit && (
          <Button type="submit" size="sm" variant="primary" icon={<Save size={16} />} isLoading={saving}>
            Guardar
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Los cambios solo aplican a {section.name}. Sus residentes pagan a esta cuenta.
      </p>
      <fieldset disabled={!canEdit || saving}>
        <SectionBankInfoFields value={bankInfo} onChange={setBankInfo} />
      </fieldset>
    </form>
  );
};
